import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Camera, Send, Paperclip, X, Loader } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { createPortal } from 'react-dom';
import { useAuth } from '../../App';
import { debugCollector } from '../../utils/DebugCollector';
import ImageLightbox from '../ImageLightbox';
import {
  addStationFeedbackComment,
  addFeedbackComment,
  getFeedbackImageUrl,
  type Feedback,
  type FeedbackComment,
  type FeedbackStatus,
} from '../../api/client';

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  OPEN: 'Offen',
  RUCKFRAGE: 'Rueckfrage',
  DONE: 'Erledigt',
  REJECTED: 'Abgelehnt',
};

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  OPEN: 'text-blue-600 bg-blue-50 border-blue-200',
  RUCKFRAGE: 'text-amber-600 bg-amber-50 border-amber-200',
  DONE: 'text-green-600 bg-green-50 border-green-200',
  REJECTED: 'text-slate-500 bg-slate-50 border-slate-200',
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

interface ChatThreadProps {
  feedback: Feedback;
  onBack: () => void;
  onCommentAdded: () => void;
}

export default function ChatThread({ feedback, onBack, onCommentAdded }: ChatThreadProps) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isStation = !!user?.stationId;
  const myId = user?.userId || user?.stationId || '';

  // Visible comments (no DEBUG_DATA)
  const visibleComments = feedback.comments.filter((c) => c.type !== 'DEBUG_DATA');

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visibleComments.length]);

  // Auto-resize textarea
  const handleTextChange = (val: string) => {
    setText(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleScreenshot = async () => {
    if (capturing) return;
    setCapturing(true);

    const drawer = document.querySelector('[data-feedback-drawer]') as HTMLElement | null;
    try {
      // Ghost-Mode: Drawer verstecken
      if (drawer) {
        drawer.style.visibility = 'hidden';
        await new Promise((r) => setTimeout(r, 150));
      }

      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: false,
        scale: 1,
      });

      // Drawer sofort wieder zeigen
      if (drawer) drawer.style.visibility = 'visible';

      canvas.toBlob((blob: Blob | null) => {
        if (!blob) {
          console.error('Canvas-Export fehlgeschlagen: Blob ist null.');
          setCapturing(false);
          return;
        }
        const file = new File([blob], `screen_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setAttachments((prev) => [...prev, file]);
        setCapturing(false);
      }, 'image/jpeg', 0.8);
    } catch (error: any) {
      console.error('Screenshot fehlgeschlagen:', error);
      if (drawer) drawer.style.visibility = 'visible';
      setCapturing(false);
    }
  };

  const handleSend = async () => {
    if (!text.trim() && attachments.length === 0) return;
    if (!user) return;

    setSending(true);
    try {
      const debugData = JSON.stringify(debugCollector.getSnapshot());
      const total = Math.max(1, attachments.length);

      const sendComment = async (msg?: string, img?: File, dbg?: string) => {
        if (isStation && user.stationId) {
          await addStationFeedbackComment(user.stationId, feedback.id, {
            message: msg, image: img, debugData: dbg,
          });
        } else {
          await addFeedbackComment(feedback.id, {
            message: msg, image: img, debugData: dbg,
          });
        }
      };

      if (total > 1) setSendProgress(`Sende 1 von ${total}...`);
      await sendComment(text.trim() || undefined, attachments[0] || undefined, debugData);

      for (let i = 1; i < attachments.length; i++) {
        setSendProgress(`Sende ${i + 1} von ${total}...`);
        await sendComment(undefined, attachments[i]);
      }

      setText('');
      setAttachments([]);
      setSendProgress('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      onCommentAdded();
    } catch (e: any) {
      console.error('Comment failed:', e);
    } finally {
      setSending(false);
      setSendProgress('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderComment = (comment: FeedbackComment) => {
    if (comment.type === 'SYSTEM_EVENT') {
      return (
        <div key={comment.id} className="flex justify-center py-1.5">
          <span className="text-[11px] text-slate-400 bg-slate-50 px-2.5 py-0.5 rounded-full">
            {comment.message}
          </span>
        </div>
      );
    }

    const isMe = comment.authorId === myId || comment.authorId === feedback.submittedById;
    const authorName = comment.author?.displayName || 'System';
    const initial = (authorName[0] || '?').toUpperCase();

    return (
      <div key={comment.id} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        <div
          className={`w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 ${
            isMe ? 'bg-emerald-500' : 'bg-blue-500'
          }`}
        >
          {initial}
        </div>

        {/* Bubble */}
        <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
          <div className={`text-[11px] mb-0.5 ${isMe ? 'text-right' : ''} text-slate-400`}>
            <span className="font-medium text-slate-600">{authorName}</span>
            <span className="ml-1.5">{formatTime(comment.createdAt)}</span>
          </div>
          <div
            className={`rounded-xl px-3 py-2 border ${
              isMe
                ? 'bg-emerald-50 border-emerald-100 rounded-tr-sm'
                : 'bg-blue-50 border-blue-100 rounded-tl-sm'
            }`}
          >
            {comment.message && (
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{comment.message}</p>
            )}
            {comment.imagePath && (
              <button
                onClick={() => setLightboxSrc(getFeedbackImageUrl(comment.imagePath!, feedback.stationId || undefined))}
                className="block mt-1 cursor-pointer"
              >
                <img
                  src={getFeedbackImageUrl(comment.imagePath, feedback.stationId || undefined)}
                  alt="Bild"
                  className="max-w-[200px] max-h-[140px] rounded-lg border border-slate-200 hover:border-blue-400 transition-colors"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" data-feedback-drawer>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50 shrink-0">
        <button onClick={onBack} className="p-1 text-slate-500 hover:text-slate-700 rounded transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-400">#{feedback.number}</span>
            <span className="text-sm font-semibold text-slate-800 truncate">{feedback.title}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${STATUS_COLORS[feedback.status]}`}>
              {STATUS_LABELS[feedback.status]}
            </span>
            <span className="text-[11px] text-slate-400">
              {feedback.submittedBy?.displayName || 'Unbekannt'}
              {feedback.station?.name ? ` · ${feedback.station.name}` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {visibleComments.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Noch keine Nachrichten</p>
        ) : (
          visibleComments.map(renderComment)
        )}
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 border-t bg-slate-50 shrink-0">
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                <div className="relative w-10 h-10 shrink-0">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    className="w-full h-full object-cover rounded bg-white"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 truncate max-w-[100px]">{file.name}</span>
                  <button
                    onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-[10px] text-red-500 hover:underline text-left"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="px-3 py-2.5 border-t bg-white shrink-0">
        <div className="flex items-end gap-2">
          {/* Screenshot */}
          <button
            onClick={handleScreenshot}
            disabled={capturing}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors shrink-0"
            title="Screenshot aufnehmen"
          >
            {capturing ? <Loader className="w-4 h-4 animate-spin text-amber-500" /> : <Camera className="w-4 h-4" />}
          </button>

          {/* File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setAttachments((prev) => [...prev, f]);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors shrink-0"
            title="Bild anhaengen"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nachricht schreiben..."
            rows={1}
            className="flex-1 resize-none border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
          />

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={sending || (!text.trim() && attachments.length === 0)}
            className="p-2 text-white bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:hover:bg-slate-700 shrink-0"
            title="Senden (Ctrl+Enter)"
          >
            <Send className={`w-4 h-4 ${sending ? 'animate-pulse' : ''}`} />
          </button>
        </div>
        <p className="text-[10px] text-slate-300 mt-1 text-right">
          {sendProgress || 'Ctrl+Enter zum Senden'}
        </p>
      </div>

      {/* Lightbox */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
